import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Analytics } from '@vercel/analytics/react';
import { API_BASE_URL, isGuestMode, clearGuestMode } from './config/api';
import { DebtWarfare } from './components/organisms/DebtWarfare';
import { AICoach } from './components/organisms/AICoach';
import { AnimatedMetric } from './components/atoms/AnimatedMetric';
import { SkullLogo } from './components/atoms/SkullLogo';
import { LandingPage } from './components/pages/LandingPage';
import { LoginPage } from './components/pages/LoginPage';
import { Arena } from './components/pages/Arena';
import { theme } from './theme';
import { app, nav, dashboard, alerts } from './language';
import { useMobile } from './hooks/useMediaQuery';

type View = 'dashboard' | 'warfare' | 'ai' | 'arena';
type AppState = 'landing' | 'login' | 'app';

const navItems: { id: View; label: string }[] = [
  { id: 'arena',     label: 'The Arena' },
  { id: 'dashboard', label: nav.dashboard },
  { id: 'warfare',   label: nav.warfare },
  { id: 'ai',        label: nav.ai },
];

function App() {
  const [appState, setAppState] = useState<AppState>('landing');
  const [user, setUser] = useState<any>(null);
  const [activeView, setActiveView] = useState<View>('arena');
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'complete'>('idle');
  const mobile = useMobile();

  // Check for existing authentication on app load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userInfo = localStorage.getItem('user_info');

    if (token && userInfo) {
      try {
        setUser(JSON.parse(userInfo));
        setAppState('app');
      } catch (err) {
        // Clear invalid stored data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_info');
      }
    }
  }, []);

  const handleLogin = (userData: any) => {
    setUser(userData);
    setAppState('app');
  };

  const handleLogout = () => {
    clearGuestMode();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
    setUser(null);
    setAppState('landing');
  };

  const handleFileUpload = async (file: File) => {
    setUploadStatus('uploading');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/analyze-warrior`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setAnalysisResult(result);
      setUploadStatus('complete');
      setTimeout(() => setActiveView('ai'), 600);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadStatus('idle');
    }
  };

  // Handle different app states
  if (appState === 'landing') {
    return <LandingPage onEnter={() => setAppState('login')} />;
  }

  if (appState === 'login') {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Main authenticated app

  const pad = mobile ? theme.spacing[4] : theme.spacing[8];

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.semantic.bg.page,
      color: theme.semantic.text.primary,
      fontFamily: theme.typography.fontFamily.sans,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ═══ DEMO BANNER ═══════════════════════════════════ */}
      {isGuestMode() && (
        <div style={{
          background: `linear-gradient(90deg, ${theme.brand.copper}, ${theme.brand.crimson})`,
          padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
          textAlign: 'center',
          fontSize: theme.typography.fontSize.xs,
          fontFamily: theme.typography.fontFamily.display,
          fontWeight: theme.typography.fontWeight.bold,
          letterSpacing: theme.typography.letterSpacing.wider,
          color: theme.brand.cream,
          textTransform: 'uppercase' as const,
        }}>
          Demo Mode — data shown is for illustration
        </div>
      )}

      {/* ═══ HEADER ═══════════════════════════════════════ */}
      <header style={{
        background: theme.semantic.bg.surface,
        borderBottom: `${theme.borders.heavy} ${theme.brand.crimson}`,
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.header,
      }}>
        <div style={{ height: '3px', background: theme.brand.copper }} />

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: `${theme.spacing[3]} ${pad}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing[3],
        }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], flexShrink: 0 }}>
            <SkullLogo size={mobile ? 28 : 36} />
            {!mobile && (
              <h1 style={{
                fontFamily: theme.typography.fontFamily.display,
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.black,
                color: theme.semantic.text.primary,
                letterSpacing: theme.typography.letterSpacing.widest,
                textTransform: 'uppercase' as const,
                margin: 0,
                lineHeight: 1,
              }}>
                {app.name}
              </h1>
            )}
          </div>

          {/* Nav */}
          <nav style={{ display: 'flex', gap: 0 }}>
            {navItems.map(({ id, label }) => {
              const active = activeView === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveView(id)}
                  style={{
                    padding: mobile
                      ? `${theme.spacing[2]} ${theme.spacing[3]}`
                      : `${theme.spacing[2]} ${theme.spacing[5]}`,
                    background: active ? theme.brand.crimson : 'transparent',
                    border: 'none',
                    color: active ? theme.brand.cream : theme.semantic.text.muted,
                    fontFamily: theme.typography.fontFamily.display,
                    fontSize: mobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm,
                    fontWeight: theme.typography.fontWeight.bold,
                    letterSpacing: theme.typography.letterSpacing.wider,
                    textTransform: 'uppercase' as const,
                    cursor: 'pointer',
                    transition: `all ${theme.transitions.fast}`,
                    minHeight: '44px',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </nav>

          {/* User Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
            {user && (
              <span style={{
                color: theme.semantic.text.muted,
                fontSize: theme.typography.fontSize.sm,
                fontFamily: theme.typography.fontFamily.sans,
              }}>
                {user.username}
              </span>
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
                background: 'transparent',
                border: `1px solid ${theme.brand.charcoal}`,
                borderRadius: theme.radii.sm,
                color: theme.semantic.text.muted,
                fontFamily: theme.typography.fontFamily.display,
                fontSize: theme.typography.fontSize.xs,
                fontWeight: theme.typography.fontWeight.bold,
                letterSpacing: theme.typography.letterSpacing.wider,
                textTransform: 'uppercase' as const,
                cursor: 'pointer',
                transition: `all ${theme.transitions.fast}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.brand.crimson;
                e.currentTarget.style.color = theme.brand.crimson;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.brand.charcoal;
                e.currentTarget.style.color = theme.semantic.text.muted;
              }}
            >
              Logout
            </button>
          </div>

          {/* Tagline — hide on mobile */}
          {!mobile && (
            <div style={{
              fontFamily: theme.typography.fontFamily.display,
              fontSize: theme.typography.fontSize.xs,
              color: theme.semantic.text.disabled,
              letterSpacing: theme.typography.letterSpacing.widest,
              textTransform: 'uppercase' as const,
              flexShrink: 0,
            }}>
              {app.tagline}
            </div>
          )}
        </div>
      </header>

      {/* ═══ MAIN ═════════════════════════════════════════ */}
      <main style={{ flex: 1 }}>
        <AnimatePresence mode="wait">

          {/* Dashboard */}
          {activeView === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ maxWidth: '1200px', margin: '0 auto', padding: pad }}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: mobile ? '1fr' : '1fr 340px',
                gap: theme.spacing[6],
              }}>
                {/* Left — Upload + Metrics */}
                <div>
                  {/* Upload */}
                  <div style={{
                    background: theme.semantic.bg.surface,
                    borderTop: `${theme.borders.heavy} ${theme.brand.crimson}`,
                    padding: mobile ? theme.spacing[4] : theme.spacing[6],
                    marginBottom: theme.spacing[6],
                  }}>
                    <div style={{
                      fontFamily: theme.typography.fontFamily.display,
                      fontSize: theme.typography.fontSize.xs,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.semantic.text.muted,
                      letterSpacing: theme.typography.letterSpacing.widest,
                      textTransform: 'uppercase' as const,
                      marginBottom: theme.spacing[3],
                      paddingBottom: theme.spacing[2],
                      borderBottom: `${theme.borders.medium} ${theme.semantic.text.muted}`,
                      display: 'inline-block',
                    }}>
                      {dashboard.upload.heading}
                    </div>

                    <p style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.semantic.text.secondary,
                      lineHeight: theme.typography.lineHeight.relaxed,
                      marginBottom: theme.spacing[4],
                    }}>
                      {dashboard.upload.description}
                    </p>

                    <label htmlFor="csv-upload" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: mobile ? theme.spacing[4] : theme.spacing[6],
                      background: theme.semantic.bg.input,
                      border: `${theme.borders.medium} ${
                        uploadStatus === 'complete' ? theme.colors.success
                        : uploadStatus === 'uploading' ? theme.brand.copper
                        : theme.semantic.border.default
                      }`,
                      cursor: 'pointer',
                      textAlign: 'center' as const,
                      minHeight: '48px',
                    }}>
                      <span style={{
                        fontFamily: theme.typography.fontFamily.display,
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.bold,
                        color: uploadStatus === 'complete'
                          ? theme.colors.success
                          : uploadStatus === 'uploading'
                            ? theme.brand.copper
                            : theme.semantic.text.muted,
                        letterSpacing: theme.typography.letterSpacing.wider,
                        textTransform: 'uppercase' as const,
                      }}>
                        {uploadStatus === 'idle' && dashboard.upload.idle.toUpperCase()}
                        {uploadStatus === 'uploading' && dashboard.upload.uploading.toUpperCase()}
                        {uploadStatus === 'complete' && dashboard.upload.complete.toUpperCase()}
                      </span>
                    </label>
                    <input
                      type="file" accept=".csv" id="csv-upload"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
                      style={{ display: 'none' }}
                    />
                    <p style={{
                      marginTop: theme.spacing[3],
                      fontSize: theme.typography.fontSize.xs,
                      color: theme.semantic.text.disabled,
                      lineHeight: theme.typography.lineHeight.relaxed,
                    }}>
                      {dashboard.upload.disclaimer}
                    </p>
                  </div>

                  {/* Quick metrics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 0,
                    border: `${theme.borders.thin} ${theme.semantic.border.default}`,
                  }}>
                    <AnimatedMetric label={dashboard.metrics.streak.label} value={7} suffix={dashboard.metrics.streak.suffix} variant="success" />
                    <AnimatedMetric label={dashboard.metrics.suffering.label} value={84} variant="warning" />
                    <AnimatedMetric label={dashboard.metrics.interest.label} value={342.50} prefix={dashboard.metrics.interest.prefix} variant="danger" />
                    <AnimatedMetric label={dashboard.metrics.readiness.label} value={67} suffix={dashboard.metrics.readiness.suffix} variant="neutral" />
                  </div>
                </div>

                {/* Sidebar — The Hard Truth */}
                <div style={{
                  background: theme.semantic.bg.surface,
                  borderTop: `${theme.borders.heavy} ${theme.brand.copper}`,
                  padding: mobile ? theme.spacing[4] : theme.spacing[5],
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    fontFamily: theme.typography.fontFamily.display,
                    fontSize: theme.typography.fontSize.xs,
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.brand.copper,
                    letterSpacing: theme.typography.letterSpacing.widest,
                    textTransform: 'uppercase' as const,
                    marginBottom: theme.spacing[4],
                    paddingBottom: theme.spacing[2],
                    borderBottom: `${theme.borders.medium} ${theme.brand.copper}`,
                    display: 'inline-block',
                  }}>
                    {dashboard.sidebar.heading}
                  </div>

                  {/* Critical alert */}
                  <div style={{
                    background: theme.brand.burgundy,
                    borderLeft: `${theme.borders.heavy} ${theme.brand.crimson}`,
                    padding: theme.spacing[4],
                    marginBottom: theme.spacing[3],
                  }}>
                    <div style={{
                      fontFamily: theme.typography.fontFamily.display,
                      fontSize: '0.65rem',
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.brand.crimson,
                      letterSpacing: theme.typography.letterSpacing.widest,
                      marginBottom: theme.spacing[1],
                    }}>
                      {alerts.critical.label}
                    </div>
                    <p style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.semantic.text.secondary,
                      lineHeight: theme.typography.lineHeight.relaxed,
                      margin: 0,
                    }}>
                      {alerts.critical.takeaway}
                    </p>
                  </div>

                  {/* Warning alert */}
                  <div style={{
                    background: theme.colors.secondary[900],
                    borderLeft: `${theme.borders.heavy} ${theme.brand.copper}`,
                    padding: theme.spacing[4],
                    marginBottom: theme.spacing[3],
                  }}>
                    <div style={{
                      fontFamily: theme.typography.fontFamily.display,
                      fontSize: '0.65rem',
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.brand.copper,
                      letterSpacing: theme.typography.letterSpacing.widest,
                      marginBottom: theme.spacing[1],
                    }}>
                      {alerts.warning.label}
                    </div>
                    <p style={{
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.semantic.text.secondary,
                      lineHeight: theme.typography.lineHeight.relaxed,
                      margin: 0,
                    }}>
                      {alerts.warning.subscriptions}
                    </p>
                  </div>

                  {/* Skull watermark — hide on mobile to save space */}
                  {!mobile && (
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      paddingTop: theme.spacing[4],
                      opacity: 0.12,
                    }}>
                      <img src="/skull.png" alt="" style={{ width: '140px', height: 'auto' }} />
                    </div>
                  )}

                  {/* Footer quote */}
                  <p style={{
                    marginTop: theme.spacing[4],
                    paddingTop: theme.spacing[3],
                    borderTop: `1px solid ${theme.semantic.border.default}`,
                    fontSize: theme.typography.fontSize.xs,
                    color: theme.semantic.text.disabled,
                    fontStyle: 'italic',
                    textAlign: 'center' as const,
                    margin: `${theme.spacing[4]} 0 0 0`,
                  }}>
                    {dashboard.sidebar.quote}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Warfare */}
          {activeView === 'warfare' && (
            <motion.div key="warfare" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
              <DebtWarfare />
            </motion.div>
          )}

          {/* AI Coach */}
          {activeView === 'ai' && (
            <motion.div key="ai" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              style={{ maxWidth: '1200px', margin: '0 auto', padding: pad }}
            >
              <AICoach analysisResult={analysisResult} />
            </motion.div>
          )}

          {/* The Arena */}
          {activeView === 'arena' && (
            <motion.div key="arena" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
              style={{ width: '100%' }}
            >
              <Arena />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* ═══ FOOTER ═══════════════════════════════════════ */}
      <footer style={{
        borderTop: `${theme.borders.thin} ${theme.semantic.border.default}`,
        padding: `${theme.spacing[3]} ${pad}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
      }}>
        <span style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: theme.typography.fontSize.xs,
          color: theme.semantic.text.disabled,
          letterSpacing: theme.typography.letterSpacing.wider,
          textTransform: 'uppercase' as const,
        }}>
          {app.name} {app.version}
        </span>
        <span style={{
          fontSize: theme.typography.fontSize.xs,
          color: theme.semantic.text.disabled,
        }}>
          {app.footer}
        </span>
      </footer>
      <Analytics />
    </div>
  );
}

export default App;
